import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  CategoryNode,
  Locale,
  ProductDetail,
  ProductStatus,
  ProductTranslations,
} from '@icetus/contracts';
import { LOCALES, LOCALE_LABEL, PRODUCT_STATUSES } from '@icetus/contracts';
import { Badge, Button } from '@icetus/ui';
import { createProduct, translateInline, updateProduct } from '../api';
import { ArtGenerator, SparkIcon } from '../components/art-generator';
import { useConfirm } from '../components/confirm';
import { EditorPage, LocaleTabs, PreviewRow } from '../components/editor-page';
import { ImageField } from '../components/image-field';
import { FrontLink } from '../components/site-link';
import { ImageUploader } from '../components/image-uploader';
import { MediaPickerButton } from '../components/media-picker';

/**
 * 商品编辑表单。
 *
 * 三个刻意的取舍：
 *
 * 1) **价格按"元"输入，按"分"提交**。库里存 integer 分（避免浮点误差），
 *    但让客户在输入框里写 1234 表示 12.34 元是不可能的。转换只在这一层做。
 *
 * 2) **图集保留"一行一个 URL"的输入方式，但旁边有上传按钮**。
 *    保留文本框是因为客户经常要粘 CDN 外链；加按钮是因为更多客户手上
 *    只有一堆相机原图，让他们先找图床是本末倒置。两者写进同一个字段，
 *    提交结构不变 —— 上传只是帮忙把 URL 填进来。
 *
 * 3) **翻译只提交填了名称的语言**。空语言不提交，才不会把客户已有的西语翻译
 *    用空字符串盖掉 —— 这是"只改出现的字段"这条规则在前端的落点。
 *
 * 形态上从"右侧抽屉"改成**整页编辑**（见 components/editor-page.tsx）。
 * 抽屉时代这条路走过一遍：720px 里塞 5 段 + 四门语言 + 5 列规格表，
 * 结果是最主要的翻译工作在一条很长的滚动条里爬。整页之后表单能到
 * 1100px 以上，语言改标签一次只看一门，右侧还有一栏实时预览。
 *
 * 代价是编辑时看不见列表，所以 `onCancel` 前要先确认有没有未保存的改动，
 * 并且返回后列表的筛选/页码/滚动位置由上层还原。
 */

/**
 * 在数组里把一项挪 delta 位。
 *
 * 越界时原样返回（而不是抛错或挪到另一头）：按钮的 disabled 已经挡住了
 * 边界，这里再"自动循环到末尾"只会让手快的客户莫名其妙地换了顺序。
 */
function moveItem<T>(items: T[], index: number, delta: number): T[] {
  const target = index + delta;
  if (target < 0 || target >= items.length) return items;
  const next = [...items];
  const [item] = next.splice(index, 1);
  next.splice(target, 0, item!);
  return next;
}

export interface VariantDraft {
  sku: string;
  price: string;
  stock: string;
  attributes: string;
}

export function ProductEditor({
  open,
  tenantId,
  siteId,
  defaultLocale,
  product,
  categories,
  previewHref,
  previewHint,
  canGenerateArt = false,
  onSaved,
  onCancel,
}: {
  open: boolean;
  tenantId: string;
  siteId: string;
  /** 站点默认语言。它用自己的主 slug 输入框，不给覆盖值 */
  defaultLocale: Locale;
  /** null = 新建 */
  product: ProductDetail | null;
  categories: CategoryNode[];
  /** 这个商品在前台的地址。null = 打不开（草稿/归档/站点没绑域名），原因见 previewHint */
  previewHref?: string | null;
  previewHint?: string;
  /**
   * 能不能用 AI 出图（按张计费，权限同"发起翻译"：admin / owner）。
   *
   * 默认 false：漏传时宁可没有入口，也别让 editor 点进去撞 403 ——
   * 服务端认的是 `site:update`，前端这层只是别让人白点。
   */
  canGenerateArt?: boolean;
  onSaved: (saved: ProductDetail) => void;
  onCancel: () => void;
}) {
  /**
   * 初始值只写一处。
   *
   * 这份"从 product 摊成表单字段"的逻辑以前有三份：useState 初始值、
   * 切换商品时的重置、以及（这次新增的）脏检查基线。三处各写一遍，
   * 加一个字段就得记得改三次，漏一次就是"打开时显示旧值"。
   */
  const initial = buildInitial(product);

  const [slug, setSlug] = useState(initial.slug);
  const [categoryId, setCategoryId] = useState(initial.categoryId);
  const [status, setStatus] = useState<ProductStatus>(initial.status);
  const [price, setPrice] = useState(initial.price);
  const [currency, setCurrency] = useState(initial.currency);
  const [coverUrl, setCoverUrl] = useState(initial.coverUrl);
  const [attributes, setAttributes] = useState(initial.attributes);
  const [imagesText, setImagesText] = useState(initial.imagesText);
  /**
   * 图集的两个视图（缩略图列表 / 多行文本框）读的是同一个字段。
   *
   * 真正的存储仍然是"一行一个 URL"的字符串：提交结构不变、后端 `parseImages`
   * 不用动，客户粘一批外链也还是最快的那条路。列表只是把它解析出来给人看。
   */
  const galleryUrls = useMemo(
    () =>
      imagesText
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean),
    [imagesText],
  );
  const setGallery = (urls: string[]) => setImagesText(urls.join('\n'));
  const [translations, setTranslations] = useState<ProductTranslations>(initial.translations);
  const [variants, setVariants] = useState<VariantDraft[]>(initial.variants);

  /** 当前正在填的语言。四门语言的数据一直在 translations 里，切标签只是换显示 */
  const [localeTab, setLocaleTab] = useState<Locale>(defaultLocale);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * 图集的 AI 生成面板。
   *
   * 封面那格不需要它：封面走 `ImageField`，那个组件自己管着开关。
   * 图集是一列地址、没有"这一格"，只能由这一页提供一个按钮，
   * 出一张就往列表末尾接一张。
   */
  const [galleryGenOpen, setGalleryGenOpen] = useState(false);
  /** 正在翻译的语言。用 Locale | null 而不是布尔：同时只翻一门，按钮要知道是哪一门在转 */
  const [translating, setTranslating] = useState<Locale | null>(null);
  /**
   * 上次翻译的一句话结果，就地显示在按钮旁边。
   *
   * 带 tone 是因为**成功也要说一声**：译文只是填进输入框，不点保存等于白翻，
   * 而"框里突然多出中文"客户未必会意识到那是刚翻的。失败更必须就地显示 ——
   * 页头那条错误在表单中部根本看不见（翻译按钮离页头隔了整整一屏）。
   */
  const [translateNote, setTranslateNote] = useState<{
    locale: Locale;
    tone: 'ok' | 'warn' | 'error';
    text: string;
  } | null>(null);
  const { confirm, confirmDialog } = useConfirm();

  // 打开另一个商品时要重置：上一次编辑到一半关掉，再打开不能带上次的输入。
  const productKey = product?.id ?? (open ? 'new' : '');
  const lastKey = useRef('');
  useEffect(() => {
    if (!open) {
      lastKey.current = '';
      return;
    }
    if (lastKey.current === productKey) return;
    lastKey.current = productKey;
    const next = buildInitial(product);
    setSlug(next.slug);
    setCategoryId(next.categoryId);
    setStatus(next.status);
    setPrice(next.price);
    setCurrency(next.currency);
    setCoverUrl(next.coverUrl);
    setAttributes(next.attributes);
    setImagesText(next.imagesText);
    setTranslations(next.translations);
    setVariants(next.variants);
    setLocaleTab(defaultLocale);
    setError(null);
    setGalleryGenOpen(false);
  }, [open, product, productKey, defaultLocale]);

  /**
   * 有没有未保存的改动。
   *
   * 整页编辑不像抽屉那样有遮罩挡着，"点返回"是第一反应，所以必须问一句。
   * 用快照比对而不是在每个 onChange 里打标记：字段有十几个，
   * 手写标记一定会漏，而漏掉的那个字段恰好是客户改了半小时的翻译。
   */
  const baseline = useMemo(() => fingerprint(buildInitial(product)), [product]);
  const dirty =
    fingerprint({
      slug,
      categoryId,
      status,
      price,
      currency,
      coverUrl,
      attributes,
      imagesText,
      translations,
      variants,
    }) !== baseline;

  const requestClose = async (): Promise<void> => {
    if (!dirty) {
      onCancel();
      return;
    }
    const ok = await confirm({
      title: '放弃未保存的修改？',
      message: <>这个商品的改动还没保存，离开后需要重新填。</>,
      confirmText: '放弃修改',
      cancelText: '继续编辑',
      tone: 'danger',
    });
    if (ok) onCancel();
  };

  const updateVariant = (index: number, patch: Partial<VariantDraft>) => {
    setVariants((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  const setTranslation = (
    locale: Locale,
    field: 'name' | 'summary' | 'description' | 'slug',
    value: string,
  ) => {
    setTranslations((prev) => {
      const current = prev[locale] ?? { name: '', summary: null, description: null };
      return { ...prev, [locale]: { ...current, [field]: value } };
    });
  };

  /**
   * 把默认语言这一份内容翻到目标语言，填进表单。
   *
   * 翻的是**表单里的当前值**，不是库里已保存的版本：客户刚改完一段描述就想看看
   * 西语该怎么说，这时候去读库只会翻出他上一次保存的东西。
   *
   * 结果只填进表单、不落库 —— 客户得点保存才生效。这样他有机会先看一眼再决定，
   * 而且不会出现"我在改上面字段，下面已经被后台悄悄写过了"。
   */
  const translateInto = async (locale: Locale): Promise<void> => {
    const source = translations[defaultLocale];
    const sourceName = source?.name?.trim() ?? '';
    if (!sourceName) {
      setTranslateNote({
        locale,
        tone: 'error',
        text: `「${LOCALE_LABEL[defaultLocale]}」的名称还空着，先回那一栏填好再来翻译`,
      });
      return;
    }

    const existing = translations[locale];
    const hasContent = Boolean(
      existing?.name?.trim() || existing?.summary?.trim() || existing?.description?.trim(),
    );
    if (hasContent) {
      const ok = await confirm({
        title: `覆盖「${LOCALE_LABEL[locale]}」现有内容？`,
        message:
          '会用默认语言现在的名称、摘要与描述重新生成，覆盖这门语言里已有的文字。标识不在翻译范围内，不受影响。',
        confirmText: '覆盖翻译',
        cancelText: '取消',
      });
      if (!ok) return;
    }

    setTranslating(locale);
    setTranslateNote(null);
    try {
      const result = await translateInline(tenantId, {
        siteId,
        targetLocale: locale,
        entityKind: 'product',
        entityId: product?.id ?? null,
        name: sourceName,
        summary: source?.summary?.trim() || null,
        description: source?.description?.trim() || null,
      });
      if (!result.name) {
        setTranslateNote({
          locale,
          tone: 'error',
          text: `没能翻出${LOCALE_LABEL[locale]}的名称，这种情况少见，再点一次通常就好`,
        });
        return;
      }
      // 逐字段写，不整块替换：标识（slug）不在翻译范围内，必须原样留着
      setTranslation(locale, 'name', result.name);
      if (result.summary) setTranslation(locale, 'summary', result.summary);
      if (result.description) setTranslation(locale, 'description', result.description);
      setTranslateNote({
        locale,
        tone: result.notes.length > 0 ? 'warn' : 'ok',
        text:
          result.notes.length > 0
            ? `已翻译成${LOCALE_LABEL[locale]}，填入 ${result.chars} 字，其中 ${result.notes.length} 处建议核对。记得点保存`
            : `已翻译成${LOCALE_LABEL[locale]}，填入 ${result.chars} 字。记得点保存`,
      });
    } catch (err) {
      setTranslateNote({
        locale,
        tone: 'error',
        text: `翻译没成功：${err instanceof Error ? err.message : String(err)}`,
      });
    } finally {
      setTranslating(null);
    }
  };

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      // 只提交有名称的语言：空格子不该把已有翻译覆盖成空
      const cleanTranslations: ProductTranslations = {};
      for (const locale of LOCALES) {
        const entry = translations[locale];
        if (!entry || !entry.name.trim()) continue;
        cleanTranslations[locale] = {
          name: entry.name.trim(),
          summary: entry.summary?.trim() || null,
          description: entry.description?.trim() || null,
          // 默认语言不带 slug：它的地址就是主 slug，服务端会强制写回 null。
          // 其他语言留空 = 清除覆盖、回落到主 slug，所以这里传 null 而不是省略：
          // 省略（undefined）会被服务端理解为"本次没提，保持原值"，客户就永远清不掉。
          ...(locale === defaultLocale ? {} : { slug: entry.slug?.trim() || null }),
        };
      }

      // slug 留空时按英文名（其次任意一门）自动转写，避免客户被"必须填标识"卡住
      const fallbackName =
        cleanTranslations.en?.name ?? Object.values(cleanTranslations)[0]?.name ?? '';
      const finalSlug = slug.trim() || toSlug(fallbackName);
      if (!finalSlug || !fallbackName) {
        setError('至少填一种语言的商品名称');
        setBusy(false);
        return;
      }

      const input = {
        slug: finalSlug,
        categoryId: categoryId || null,
        status,
        priceCents: price.trim() === '' ? null : Math.round(Number(price) * 100),
        currency: currency.trim().toUpperCase(),
        coverUrl: coverUrl.trim() || null,
        attributes: textToKv(attributes),
        translations: cleanTranslations,
        images: imagesText
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean)
          .map((url) => ({ url })),
        variants: variants
          .filter((v) => v.sku.trim())
          .map((v) => ({
            sku: v.sku.trim(),
            priceCents: v.price.trim() === '' ? null : Math.round(Number(v.price) * 100),
            stock: v.stock.trim() === '' ? null : Number(v.stock),
            attributes: textToKv(v.attributes),
          })),
      };

      const saved = product
        ? await updateProduct(tenantId, product.id, input)
        : await createProduct(tenantId, siteId, input);
      onSaved(saved);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  // 父组件始终挂着这个组件、只翻 open；整页编辑没有浮层自己那层"打开才渲染"，
  // 所以这里必须自己挡住，否则列表页下面会一直蹲着一个编辑页
  if (!open) return null;

  const active = translations[localeTab];
  const activeName = active?.name?.trim() ?? '';
  const activeSummary = active?.summary?.trim() ?? '';
  /**
   * AI 出图面板里预填的"画什么"：商品名。
   *
   * 图不分语言（同一张给四门语言共用），所以优先主语言那一份；主语言还空着时
   * 退到任意一门已填的 —— 客户切到英文页签填完名字再回头点封面上的 AI，
   * 拿到一个空白的"画什么"会以为这功能坏了。
   */
  const artSubject = productSubject(translations, defaultLocale);
  // 地址预览按"这门语言实际会生成的地址"算：默认语言用主 slug，
  // 其他语言优先自身覆盖值。客户改完标识能立刻看到地址变成什么。
  const effectiveSlug = (localeTab === defaultLocale ? slug : active?.slug?.trim() || slug).trim();
  const previewPath = `/${localeTab === defaultLocale ? '' : `${localeTab}/`}products/${effectiveSlug || '…'}`;
  const filledLocales = Object.fromEntries(
    LOCALES.map((locale) => [locale, Boolean(translations[locale]?.name?.trim())]),
  ) as Partial<Record<Locale, boolean>>;
  const priceNumber = Number(price);
  const priceText =
    price.trim() === ''
      ? '面议'
      : Number.isFinite(priceNumber)
        ? `${currency || 'USD'} ${priceNumber.toFixed(2)}`
        : `${currency} ${price}`;

  return (
    <>
      <EditorPage
        title={product ? `编辑：${product.name ?? product.slug}` : '新建商品'}
        description={
          product
            ? '改动保存后立即生效。草稿不会出现在前台与 sitemap 里。'
            : '至少填一种语言的名称。标识留空会按英文名自动生成。'
        }
        meta={<Badge tone={statusTone(status)}>{statusLabel(status)}</Badge>}
        onBack={() => void requestClose()}
        actions={
          <>
            {error ? (
              <span className="error editor__error" title={error}>
                {error}
              </span>
            ) : null}
            {/*
              新建商品没有前台地址，按钮直接不出现：
              一个灰着的"看前台"对还没保存过的东西没有任何意义。

              有未保存改动时也禁用。前台读的是库里已保存的版本，
              这时点开会看到旧内容，客户十有八九会以为"我改的没生效"，
              然后回头再改一遍。宁可让他先保存（就一次点击），
              也不要让他对着旧页面怀疑自己。
            */}
            {product ? (
              <FrontLink
                href={previewHref && !dirty ? previewHref : null}
                label="看前台"
                variant="ghost"
                ariaLabel={`在新窗口打开「${product.name ?? product.slug}」的前台页面`}
                unavailableHint={dirty ? '有未保存的改动，先保存再看前台' : previewHint}
                hint={previewHint}
              />
            ) : null}
            <Button variant="ghost" onClick={() => void requestClose()} disabled={busy}>
              取消
            </Button>
            <Button onClick={() => void submit()} disabled={busy}>
              {busy ? '保存中…' : '保存'}
            </Button>
          </>
        }
        aside={
          <div className="preview">
            <div className="preview__top">
              <span className="preview__kicker">前台效果（近似）</span>
              <Badge tone={statusTone(status)}>{statusLabel(status)}</Badge>
            </div>

            <div className="preview__cover">
              {coverUrl ? (
                <img src={coverUrl} alt="" />
              ) : (
                <span className="preview__coverempty">还没有封面</span>
              )}
            </div>

            <h3 className="preview__name">
              {activeName || <span className="preview__empty">（未填名称）</span>}
            </h3>
            <p className="preview__summary">
              {activeSummary || <span className="preview__empty">还没有摘要</span>}
            </p>

            <div className="preview__rows">
              <PreviewRow label="分类">{categoryLabel(categories, categoryId)}</PreviewRow>
              <PreviewRow label="价格">{priceText}</PreviewRow>
              <PreviewRow label="语言">{LOCALE_LABEL[localeTab]}</PreviewRow>
              <PreviewRow label="地址">
                <code>{previewPath}</code>
              </PreviewRow>
            </div>

            <div className="preview__langs">
              {LOCALES.map((locale) => (
                <span
                  key={locale}
                  className={`preview__lang${translations[locale]?.name?.trim() ? ' is-filled' : ''}`}
                >
                  {LOCALE_LABEL[locale]}
                </span>
              ))}
            </div>

            <p className="preview__note">
              这是按当前表单值拼出的近似效果，最终以客户站前台为准。
            </p>
          </div>
        }
      >
      <div className="form">
        {/* ── 基本信息 ─────────────────────────────────── */}
        <section className="form__section">
          <h3 className="form__h">基本信息</h3>
          <div className="form__grid">
            <label className="stack">
              <span>URL 标识</span>
              <input
                className="field"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="aluminum-housing-6061"
              />
              {/*
                半句解释放在输入框下面（与「状态」下面那句同一套写法），
                不塞进标签：标签会撑成"标识（URL 用，留空则按英文名生成）"那样
                一整行，三个字段的标签高度就不齐了。
              */}
              <span className="muted">留空则按英文名生成。</span>
            </label>
            <label className="stack">
              <span>分类</span>
              <select className="field" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                <option value="">（未分类）</option>
                {flatten(categories).map((node) => (
                  <option key={node.id} value={node.id}>
                    {'— '.repeat(node.depth)}
                    {node.translations['en']?.name ??
                      Object.values(node.translations)[0]?.name ??
                      node.slug}
                  </option>
                ))}
              </select>
            </label>
            <label className="stack">
              <span>状态</span>
              <select
                className="field"
                value={status}
                onChange={(e) => setStatus(e.target.value as ProductStatus)}
              >
                {PRODUCT_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {statusLabel(s)}
                  </option>
                ))}
              </select>
              <span className="muted">只有「已上架」会出现在前台。</span>
            </label>
          </div>
        </section>

        {/* ── 价格 ─────────────────────────────────────── */}
        <section className="form__section">
          <h3 className="form__h">价格</h3>
          <div className="form__grid form__grid--narrow">
            <label className="stack">
              <span>价格（留空 = 面议）</span>
              <input
                className="field"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="12.50"
                inputMode="decimal"
              />
            </label>
            <label className="stack">
              <span>货币</span>
              <input
                className="field"
                value={currency}
                maxLength={3}
                onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                placeholder="USD"
              />
            </label>
          </div>
        </section>

        {/* ── 图片与属性 ────────────────────────────────── */}
        <section className="form__section">
          <h3 className="form__h">图片与属性</h3>
          <div className="form__grid form__grid--media">
            <div className="field-group">
              <span>封面图</span>
              {/*
                封面这一格用统一的 `ImageField`（地址 + 媒体库 + 上传 + AI 生成），
                而不是再拼一遍三个按钮：四个入口的摆位、禁用态、AI 出图后怎么回填，
                那个组件里已经定过一遍，抄一遍迟早有一处跟它不一样。

                `aspect: 'square'` —— 商品详情的主图框是 1:1（`product__image`），
                列表卡是 16:10。方图在卡片里裁掉的是上下留白，横图在详情页里
                裁掉的是左右两侧的主体，商品图该迁就的是详情页那一格。
              */}
              <ImageField
                value={coverUrl}
                onChange={setCoverUrl}
                tenantId={tenantId}
                siteId={siteId}
                ratio="wide"
                maxLength={2000}
                emptyLabel="还没有封面"
                uploadLabel="上传封面"
                art={
                  canGenerateArt
                    ? {
                        tenantId,
                        siteId,
                        aspect: 'square',
                        defaultStyle: 'product',
                        noun: '商品图',
                        subjectHint: artSubject,
                      }
                    : undefined
                }
              />
            </div>
            <label className="stack field-group">
              <span>属性（每行一个，形如 material=6061-T6）</span>
              <textarea
                className="field"
                rows={5}
                value={attributes}
                onChange={(e) => setAttributes(e.target.value)}
              />
            </label>
            {/* 图集跨整行：12 条 URL 挤在半栏里只能看到一个尾巴，
                客户改第 9 张图都得先拖滚动条 */}
            <div className="field-group field-group--full">
              <span>图集（最多 12 张）</span>
              {/*
                缩略图列表 + 排在前面的顺序控制，而不是只给一个文本框：
                客户在这里真正想做的事是"把第 5 张挪到第 1 张"和"删掉这张"，
                而这两件事在多行 URL 的文本框里都得先数行号。
                文本框留着（批量粘贴 CDN 外链仍然是最快的路径），
                它和缩略图列表读写同一个字段，改成哪个另一边都会跟着变。
              */}
              <div className="imglist">
                {galleryUrls.map((url, index) => (
                  <div className="imglist__row" key={`${url}-${index}`}>
                    <img className="imglist__thumb" src={url} alt="" loading="lazy" />
                    <span className="imglist__index">{index + 1}</span>
                    <div className="imglist__ops">
                      <Button
                        variant="ghost"
                        disabled={index === 0}
                        onClick={() => setGallery(moveItem(galleryUrls, index, -1))}
                      >
                        上移
                      </Button>
                      <Button
                        variant="ghost"
                        disabled={index === galleryUrls.length - 1}
                        onClick={() => setGallery(moveItem(galleryUrls, index, 1))}
                      >
                        下移
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => setGallery(galleryUrls.filter((_, i) => i !== index))}
                      >
                        移除
                      </Button>
                    </div>
                  </div>
                ))}
                {galleryUrls.length === 0 ? (
                  <p className="muted">还没有图集图片。</p>
                ) : null}
              </div>
              <div className="imgfield__actions">
                <MediaPickerButton
                  tenantId={tenantId}
                  siteId={siteId}
                  multiple
                  label="从媒体库添加"
                  onPick={(items) => {
                    // 上限 12 与后端 parseImages 的截断保持一致：
                    // 前端先截，客户能立刻看到"多了几张没进去"
                    setGallery([...galleryUrls, ...items.map((item) => item.url)].slice(0, 12));
                  }}
                />
                <ImageUploader
                  tenantId={tenantId}
                  siteId={siteId}
                  label="上传图集"
                  onUploaded={(items) => {
                    setGallery([...galleryUrls, ...items.map((item) => item.url)].slice(0, 12));
                  }}
                />
                {/*
                  图集的 AI 生成。图满 12 张时按钮灰掉并说明原因 ——
                  服务端 `parseImages` 会截断，让客户画完才发现没进去更糟。
                */}
                {canGenerateArt ? (
                  <Button
                    variant="primary"
                    className="btn-ai"
                    disabled={galleryUrls.length >= 12}
                    title={
                      galleryUrls.length >= 12 ? '图集已满 12 张，先移除一张再生成' : undefined
                    }
                    onClick={() => setGalleryGenOpen(true)}
                  >
                    <SparkIcon />
                    {'用 AI 生成'}
                  </Button>
                ) : null}
              </div>
              <textarea
                className="field"
                rows={4}
                aria-label="图集地址，每行一个"
                value={imagesText}
                onChange={(e) => setImagesText(e.target.value)}
                placeholder="也可以直接粘贴地址，每行一个"
              />
            </div>
          </div>
        </section>

        {/* ── 规格矩阵 ──────────────────────────────────── */}
        <section className="form__section">
          <h3 className="form__h">规格矩阵</h3>
          <p className="muted form__hint">
            同一款商品的不同材质/颜色。价格留空表示与主商品同价；库存留空表示不跟踪库存。
          </p>
          {variants.length > 0 ? (
            <div className="tablewrap">
              <table className="table table--variants">
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>价格</th>
                    <th>库存</th>
                    <th>属性</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {variants.map((variant, index) => (
                    <tr key={index}>
                      <td>
                        <input
                          className="field"
                          value={variant.sku}
                          onChange={(e) => updateVariant(index, { sku: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          className="field"
                          value={variant.price}
                          onChange={(e) => updateVariant(index, { price: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          className="field"
                          value={variant.stock}
                          onChange={(e) => updateVariant(index, { stock: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          className="field"
                          value={variant.attributes}
                          placeholder="material=6061; finish=anodized"
                          onChange={(e) => updateVariant(index, { attributes: e.target.value })}
                        />
                      </td>
                      <td>
                        <Button
                          variant="subtle"
                          size="sm"
                          onClick={() => setVariants((prev) => prev.filter((_, i) => i !== index))}
                        >
                          删除
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          <div className="actions">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setVariants((prev) => [...prev, { sku: '', price: '', stock: '', attributes: '' }])}
            >
              添加规格
            </Button>
          </div>
        </section>

        {/* ── 多语言文案 ────────────────────────────────── */}
        <section className="form__section">
          <h3 className="form__h">多语言文案</h3>
          <p className="muted form__hint">
            只填了名称的语言才会被保存；留空表示这门语言还没翻译，前台会回退到默认语言。
            标识就是地址最后一段，例如英文站 <code>/en/products/aluminum-housing</code>，
            留空则沿用上方主标识。
          </p>
          <LocaleTabs value={localeTab} onChange={setLocaleTab} filled={filledLocales} />
          {LOCALES.filter((locale) => locale === localeTab).map((locale) => {
            const note = translateNote?.locale === locale ? translateNote : null;
            // 这里不再写 <legend>：上面的语言页签已经写明正在编辑哪门语言，
            // 标题行只是把同样的字再说一遍，白占一整行高度。
            // fieldset 仍要一个可访问名，所以给 aria-label。
            return (
            <fieldset className="group" key={locale} aria-label={LOCALE_LABEL[locale]}>
              {/*
                主语言这一栏不放翻译按钮：它是"源"，从别处翻过来没有意义。
                其他语言各放一个 —— 客户加完商品想让三门语言都有文案，
                切一次标签点一下，比去总览页发一次全站任务快得多。

                这一块刻意做成带品牌色的方块 + 主按钮：第一版是虚线框里一颗
                灰色小按钮，读起来像"辅助说明"，实际上它是这一栏最主要的动作。
              */}
              {locale === defaultLocale ? null : (
                <div className={`translate-row${note ? ` is-${note.tone}` : ''}`}>
                  <Button
                    disabled={translating !== null || busy}
                    onClick={() => void translateInto(locale)}
                  >
                    {translating === locale ? (
                      '正在翻译…'
                    ) : (
                      <>
                        <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
                          <path
                            fill="currentColor"
                            d="M8 1.6l1.55 4.05L13.6 7.2l-4.05 1.55L8 12.8 6.45 8.75 2.4 7.2l4.05-1.55z"
                          />
                          <path fill="currentColor" opacity=".55" d="M12.9 10.6l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z" />
                        </svg>
                        翻译为{LOCALE_LABEL[locale]}
                      </>
                    )}
                  </Button>
                  <p
                    className="translate-row__note"
                    role={note?.tone === 'error' ? 'alert' : 'status'}
                  >
                    {note
                      ? note.text
                      : `按 ${LOCALE_LABEL[defaultLocale]} 的名称、摘要、描述自动生成，翻完可以再手改`}
                  </p>
                </div>
              )}
              <div className="form__grid">
                <label className="stack">
                  <span>名称</span>
                  <input
                    className="field"
                    value={translations[locale]?.name ?? ''}
                    onChange={(e) => setTranslation(locale, 'name', e.target.value)}
                  />
                </label>
                <label className="stack">
                  <span>摘要（列表页与搜索结果用）</span>
                  <input
                    className="field"
                    value={translations[locale]?.summary ?? ''}
                    onChange={(e) => setTranslation(locale, 'summary', e.target.value)}
                  />
                </label>
              </div>
              <label className="stack">
                <span>详情描述（纯文本，换行会保留）</span>
                <textarea
                  className="field"
                  rows={3}
                  value={translations[locale]?.description ?? ''}
                  onChange={(e) => setTranslation(locale, 'description', e.target.value)}
                />
              </label>
              <label className="stack">
                <span>
                  {LOCALE_LABEL[locale]} 标识
                  {locale === defaultLocale ? '（同上方的标识）' : '（留空则沿用主标识）'}
                </span>
                {locale === defaultLocale ? (
                  // 默认语言的地址就是主 slug。这里只显示不给改 ——
                  // 两个输入框写同一个字段，客户改完会以为没生效
                  <input className="field" value={slug} disabled placeholder="同上方的标识" />
                ) : (
                  <input
                    className="field"
                    value={translations[locale]?.slug ?? ''}
                    placeholder={`留空沿用「${slug || '主标识'}」`}
                    onChange={(e) => setTranslation(locale, 'slug', e.target.value)}
                  />
                )}
              </label>
            </fieldset>
            );
          })}
        </section>
      </div>
      </EditorPage>
      {/*
        图集的生成面板盖在编辑页之上，不替换它：客户在出图期间还要能看见
        自己填的名称和图集里已有的图，回来时草稿一个字都没动。
      */}
      <ArtGenerator
        open={galleryGenOpen}
        onClose={() => setGalleryGenOpen(false)}
        tenantId={tenantId}
        siteId={siteId}
        aspect="square"
        defaultSubject={artSubject}
        defaultStyle="product"
        noun="商品图"
        onApply={({ imageUrl }) => {
          // 接在末尾而不是插到最前：图集第一张是主图，客户想换成主图时
          // 已经能从封面那格换；这里生成的多半是"再加一个角度"
          setGallery([...galleryUrls, imageUrl].slice(0, 12));
        }}
      />
      {confirmDialog}
    </>
  );
}

/** 表单初始值：新建时全是空，编辑时从商品摊开 */
function buildInitial(product: ProductDetail | null): {
  slug: string;
  categoryId: string;
  status: ProductStatus;
  price: string;
  currency: string;
  coverUrl: string;
  attributes: string;
  imagesText: string;
  translations: ProductTranslations;
  variants: VariantDraft[];
} {
  return {
    slug: product?.slug ?? '',
    categoryId: product?.categoryId ?? '',
    status: product?.status ?? 'draft',
    price:
      product?.priceCents !== null && product?.priceCents !== undefined
        ? (product.priceCents / 100).toFixed(2)
        : '',
    currency: product?.currency ?? 'USD',
    coverUrl: product?.coverUrl ?? '',
    attributes: kvToText(product?.attributes ?? {}),
    imagesText: (product?.images ?? []).map((i) => i.url).join('\n'),
    translations: product?.translations ?? {},
    variants: (product?.variants ?? []).map((v) => ({
      sku: v.sku,
      price: v.priceCents === null ? '' : (v.priceCents / 100).toFixed(2),
      stock: v.stock === null ? '' : String(v.stock),
      attributes: kvToText(v.attributes),
    })),
  };
}

/**
 * 表单指纹，用于判断"有没有未保存的改动"。
 *
 * 用**数组**而不是对象字面量：对象的键序在多次 spread 后不保证稳定，
 * 顺序一变 JSON 字符串就不同 —— 表现是"什么都没改也弹放弃确认"。
 * 数组顺序由这里显式决定，不会漂。
 */
function fingerprint(v: {
  slug: string;
  categoryId: string;
  status: string;
  price: string;
  currency: string;
  coverUrl: string;
  attributes: string;
  imagesText: string;
  translations: ProductTranslations;
  variants: VariantDraft[];
}): string {
  return JSON.stringify([
    v.slug,
    v.categoryId,
    v.status,
    v.price,
    v.currency,
    v.coverUrl,
    v.attributes,
    v.imagesText,
    // 按 LOCALES 的固定顺序摊开，不用 translations 对象本身：
    // 键序会随"先填哪门语言"变化，直接序列化会出现"没改也算脏"
    LOCALES.map((locale) => {
      const entry = v.translations[locale];
      return [
        locale,
        entry?.name ?? '',
        entry?.summary ?? '',
        entry?.description ?? '',
        entry?.slug ?? '',
      ];
    }),
    v.variants.map((variant) => [variant.sku, variant.price, variant.stock, variant.attributes]),
  ]);
}

function statusLabel(status: ProductStatus): string {
  return status === 'active' ? '已上架' : status === 'archived' ? '已归档' : '草稿';
}

function statusTone(status: ProductStatus): 'success' | 'warn' | 'neutral' {
  if (status === 'active') return 'success';
  if (status === 'draft') return 'warn';
  return 'neutral';
}

/** 分类树 → 带缩进深度的扁平列表，供下拉框使用 */
function flatten(nodes: CategoryNode[], depth = 0): Array<CategoryNode & { depth: number }> {
  return nodes.flatMap((node) => [
    { ...node, depth },
    ...flatten(node.children, depth + 1),
  ]);
}

/** 预览栏显示的分类名：优先英文，其次任意已翻译语言，最后退回 slug */
function categoryLabel(nodes: CategoryNode[], id: string): string {
  if (!id) return '未分类';
  const found = flatten(nodes).find((node) => node.id === id);
  if (!found) return '未分类';
  return found.translations['en']?.name ?? Object.values(found.translations)[0]?.name ?? found.slug;
}

/**
 * AI 出图默认画什么：商品名。
 *
 * 不拼"商品图"之类的词：模型要的是**具体的东西**，名称本来就够具体；
 * 加一张空的就退回空串，由面板提示客户自己填（面板里那句"写成具体的东西"）。
 */
export function productSubject(
  translations: ProductTranslations,
  defaultLocale: Locale,
): string {
  const primary = translations[defaultLocale]?.name?.trim();
  if (primary) return primary;
  for (const entry of Object.values(translations)) {
    const name = entry?.name?.trim();
    if (name) return name;
  }
  return '';
}

function kvToText(map: Record<string, string>): string {
  return Object.entries(map)
    .map(([k, v]) => `${k}=${v}`)
    .join('\n');
}

function textToKv(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const line of text.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const index = trimmed.indexOf('=');
    if (index <= 0) continue;
    out[trimmed.slice(0, index).trim()] = trimmed.slice(index + 1).trim();
  }
  return out;
}

/** 与服务端同一套归一化规则：中文/空格 → 连字符。前端先做一次，报错更少 */
function toSlug(value: string): string {
  return value
    .normalize('NFKD')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}
